import { createSignal } from "solid-js";

/**
 * ACTIVE restaurant (organization) per user — shared state at module level
 * so that ALL instances of `useActiveContext` (one per page +
 * header switcher) react to the same change, and persisted in
 * localStorage so it survives reloads.
 *
 * The key is a `userId → orgId` map (not a flat value) because the app is
 * multi-account: each account remembers ITS active restaurant and an account
 * switch does not inherit another's pointer (same spirit as
 * workspace-switcher, hardened for multi-account).
 *
 * The pointer is only a PREFERENCE: `useActiveContext` validates it against
 * orgs actually synced locally and falls back to the first if it no longer exists
 * (deleted org / stale data) — never blocks, just like the ticket pointer
 * of the POS (self-healing).
 */
const STORAGE_KEY = "activeRestaurantByUser";

type ActiveMap = Readonly<Record<string, string>>;

function readPersisted(): ActiveMap {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const entries = Object.entries(parsed).filter(
        (entry): entry is [string, string] => typeof entry[1] === "string",
      );
      return Object.fromEntries(entries);
    }
  } catch {
    // localStorage can fail (private mode) or the JSON can be corrupt; the
    // in-memory state is sufficient and the fallback is "first org".
  }
  return {};
}

const [activeMap, setActiveMap] = createSignal<ActiveMap>(readPersisted());

/** Active restaurant pointer for the user (may point to a no longer existing org). */
export function activeRestaurantIdFor(userId: string): string | null {
  return activeMap()[userId] ?? null;
}

/** Sets (and persists) the user's active restaurant. */
export function setActiveRestaurantIdFor(userId: string, orgId: string): void {
  const next = { ...activeMap(), [userId]: orgId };
  setActiveMap(next);
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Best-effort persistence; the in-memory signal has already changed.
  }
}
